import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Header } from "@/components/site/Header";
import { HeroChicago } from "@/components/site/HeroChicago";
import { NorwoodQuiz } from "@/components/site/NorwoodQuiz";
import { ValueProps } from "@/components/site/ValueProps";
import { Footer } from "@/components/site/Footer";
import { WhyChooseChicago } from "@/components/site/WhyChooseChicago";
import eddieOlczyk from "@/assets/restore/eddie-olczyk.png.asset.json";
import harrisScott from "@/assets/restore/dr-harris-scott.webp.asset.json";
import { denverReviews } from "@/lib/denver-reviews";
import { doctors } from "@/lib/site-data";
import { safeJsonLd } from "@/lib/structured-data";
import { getDenverStructuredData } from "@/lib/structured-data/denver";
import { useLocalQuoteLinks } from "@/hooks/use-local-quote-links";

const Results = lazy(() => import("@/components/site/Results").then(m => ({ default: m.Results })));
const Reviews = lazy(() => import("@/components/site/Reviews").then(m => ({ default: m.Reviews })));
const NoShaveFuture = lazy(() => import("@/components/site/NoShaveFuture").then(m => ({ default: m.NoShaveFuture })));
const NoShaveWhy = lazy(() => import("@/components/site/NoShaveWhy").then(m => ({ default: m.NoShaveWhy })));
const Guarantee = lazy(() => import("@/components/site/Guarantee").then(m => ({ default: m.Guarantee })));
const Pricing = lazy(() => import("@/components/site/Pricing").then(m => ({ default: m.Pricing })));
const DoctorBio = lazy(() => import("@/components/site/DoctorBio").then(m => ({ default: m.DoctorBio })));
const Surgeons = lazy(() => import("@/components/site/Surgeons").then(m => ({ default: m.Surgeons })));
const DoctorsGrid = lazy(() => import("@/components/site/DoctorsGrid").then(m => ({ default: m.DoctorsGrid })));
const LocationsMapDowntownDenver = lazy(() => import("@/components/site/LocationsMapDowntownDenver").then(m => ({ default: m.LocationsMapDowntownDenver })));
const VirtualConsult = lazy(() => import("@/components/site/VirtualConsult").then(m => ({ default: m.VirtualConsult })));
const Faq = lazy(() => import("@/components/site/Faq").then(m => ({ default: m.Faq })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));

export const Route = createFileRoute("/locations/restore-denver")({
  head: () => ({
    meta: [
      { title: "Get Your Hair Back | RESTORE® Hair Transplants — Denver" },
      {
        name: "description",
        content:
          "Restore your confidence with the top hair restoration team serving Denver and the surrounding Colorado metro. Over 20,000 procedures.",
      },
      { property: "og:title", content: "Get Your Hair Back | RESTORE® Hair Transplants — Denver" },
      {
        property: "og:description",
        content:
          "RESTORE® Hair transplants in Denver and the surrounding Colorado metro. Trusted by athletes and broadcasters for natural, undetectable results.",
      },
      { property: "og:url", content: "https://restorehair.com/locations/restore-denver/" },
    ],
    links: [{ rel: "canonical", href: "https://restorehair.com/locations/restore-denver/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd({ "@graph": getDenverStructuredData() }),
      },
    ],
  }),
  component: Index,
});

function Index() {
  useLocalQuoteLinks();
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <HeroChicago
          eyebrow="RESTORE® | Denver Hair Transplants"
          subheading="RESTORE your confidence with the #1 hair restoration team in Denver"
          extraParagraph="Find out about your hair transplant options in Denver."
        />
        <NorwoodQuiz />
        <WhyChooseChicago />
        <Suspense fallback={null}>
          <div className="hidden md:block">
            <Results
              showDisclaimer
              resultsHref="https://restorehair.com/results/"
              resultsButtonLabel="See All Before & Afters"
            />
          </div>
          <ValueProps
            hideEyebrowMobile
            imageSrc={harrisScott.url}
            imageAlt="Dr. James Harris, RESTORE's Medical Director, with Scott C., RESTORE client"
            videoUrl="https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/F.U.E.ExplainedRESTOREHairWhatisFUE.mp4"
          />
          <NoShaveFuture />
          <NoShaveWhy dougLeft />
          <Reviews
            items={denverReviews}
            eyebrow="Denver Hair Transplant & Hair Restoration Testimonials"
            description="RESTORE® has changed the lives of thousands of people struggling with hair loss. Here's what they have to say about their results. No wonder our clients in Denver have chosen RESTORE as #1 in hair transplants."
          />
          <Guarantee />
          <Pricing imageSrc={eddieOlczyk.url} imageAlt="Eddie Olczyk, U.S. Hockey Hall of Fame & RESTORE Client" hidePlayOverlay />
          <DoctorBio />
          <Surgeons eyebrow="RESTORE Denver Hair Transplant Doctors" heading={<>A World Renowned <span className="text-foreground">Team</span></>} />
          <DoctorsGrid doctors={doctors.filter((d) => ["Dr. James Harris", "Dr. Shelly Lovitt"].includes(d.name))} />
          <LocationsMapDowntownDenver />
          <Faq />
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
